/**
 * 产品 KPI 的显示口径（旧 composables/analytics/useProductKpi.ts、views/analytics/LearningAnalytics.vue）
 */
import type { EChartsCoreOption } from 'echarts/core'
import type { DashboardSeriesResponse } from '@/api/metrics'

const METRIC_LABELS: Record<string, string> = {
  w4_retention: 'W4 留存',
  m3_retention: 'M3 留存',
  i_plus_one_hit_rate: 'i+1 命中率',
  daily_familiarity_net_change: '每日熟悉度净变化'
}

// 留存率与 i+1 命中率是 0–1 的比例，按百分比显示
const PERCENT_METRICS = new Set(['w4_retention', 'm3_retention', 'i_plus_one_hit_rate'])

export function metricLabel(name: string): string {
  return METRIC_LABELS[name] || name
}

export function isPercentMetric(name: string): boolean {
  return PERCENT_METRICS.has(name)
}

/** 当前值：最近一个有 value 的快照点 */
export function currentValueOf(series: DashboardSeriesResponse | null | undefined): number | null {
  const points = series?.series ?? []
  for (let index = points.length - 1; index >= 0; index--) {
    const value = points[index].value
    if (value !== null && value !== undefined) return value
  }
  return null
}

/** 百分比指标保留 1 位小数；数值指标保留正负号，非整数保留 2 位小数 */
export function formatMetricValue(metricName: string, value: number | null): string {
  if (value === null || value === undefined) return '—'
  if (isPercentMetric(metricName)) return `${(value * 100).toFixed(1)}%`
  return Number.isInteger(value) ? `${value}` : value.toFixed(2)
}

/** 单个指标的折线图；值为 null 的点断开不连线，y 轴不设下限（保留负数） */
export function buildKpiChartOption(series: DashboardSeriesResponse): EChartsCoreOption {
  const isPercent = isPercentMetric(series.metric_name)
  return {
    grid: { left: 50, right: 16, top: 12, bottom: 32 },
    tooltip: {
      trigger: 'axis',
      formatter: (params: any[]) => {
        const point = params[0]
        if (point.value?.[1] === null || point.value?.[1] === undefined) {
          return `${point.value?.[0] ?? point.name}<br/>暂无数据`
        }
        const value = point.value[1]
        const display = isPercent ? `${(value * 100).toFixed(2)}%` : value
        return `${point.value[0]}<br/>${display}`
      }
    },
    xAxis: {
      type: 'category',
      data: series.series.map((point) => point.snapshot_date),
      axisLabel: { fontSize: 10 }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 10,
        formatter: (value: number) => (isPercent ? `${(value * 100).toFixed(0)}%` : `${value}`)
      }
    },
    series: [
      {
        type: 'line',
        smooth: true,
        showSymbol: true,
        data: series.series.map((point) => [point.snapshot_date, point.value]),
        connectNulls: false
      }
    ]
  }
}
